import {iChartItemShort, ID, Timeframe} from '@models';
import {useFiltersContext} from '@core/FiltersContext';
import {useCallback, useEffect, useMemo, useState} from "react";
import {iSeries} from "@components/TimeframesChart/TimeframesChart";
import {COLORS} from "@core/constants";
import {useFilterSort} from "@hooks/useFilterSort";
import ValuesByTimeframeData, {ApiModel} from "@models/ValuesByTimeframeData";

const useAnalyzePerformanceMarkup = <T extends Record<string, any>>({
    data,
    selectedPeriod,
}: {
    data: T[] | null;
    selectedPeriod: Timeframe;
}) => {
    const {
        defaultFiltersPayload,
    } = useFiltersContext();

    const [chartItems, setChartItems] = useState<iChartItemShort[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<ID[]>([]);

    const {data: sortedData} = useFilterSort(
        data || [],
        'total' as keyof T,
        'name' as keyof T
    );

    const chartData = useMemo((): iSeries[] => {
        return chartItems.map((i) => {
            const item = (data || []).find(item => i.id === item.id);

            return {
                ...i,
                values: new ValuesByTimeframeData({apiModel: item?.data as ApiModel, timeframe: selectedPeriod}),
            };
        })
    }, [data, chartItems, selectedPeriod]);

    useEffect(() => {
        if (sortedData.length > 0 && chartItems.length === 0) {
            setChartItems(sortedData.slice(0, 5).map((i, index) => ({
                color: COLORS[index],
                id: i.id,
                title: i.name,
            } as iChartItemShort)));
        }
    }, [sortedData, chartItems.length]);

    useEffect(() => {
        setChartItems([]);
    }, [defaultFiltersPayload]);

    const addChartItem = useCallback((id: ID) => {
        const item = (data || []).find(i => i.id === id)!;
        const color = COLORS.find(c => chartItems.every(i => i.color !== c)) || 'black';

        setChartItems([
            ...chartItems,
            {
                id: item.id,
                title: item.name,
                color,
            } as iChartItemShort,
        ]);
    }, [data, chartItems]);

    const removeChartItem = useCallback((id: ID) => {
        const itemIndex = chartItems.findIndex(i => i.id === id)!;

        setChartItems([
            ...chartItems.slice(0, itemIndex),
            ...chartItems.slice(itemIndex + 1),
        ]);
    }, [chartItems]);

    return useMemo(() => ({
        chartData,
        chartItems,
        addChartItem,
        removeChartItem,
        selectedCategories,
        setSelectedCategories,
    }), [
        chartData,
        chartItems,
        addChartItem,
        removeChartItem,
        selectedCategories,
        setSelectedCategories,
    ])
};

export default useAnalyzePerformanceMarkup;